import React, { Suspense, useState, useEffect, useLayoutEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { Toaster } from "react-hot-toast";
import { store } from "./store";
import ProtectedRoute from "./utils/ProtectedRoute";
import SplashScreen from "./components/SplashScreen";
import SubscriptionModal from "./components/SubscriptionModal";
import RewardModal from "./components/common/RewardModal";
import ConfirmationModal from "./components/common/ConfirmationModal";
import WelcomeRewardGate from "./features/Home/WelcomeRewardGate";
import PwaInstallSuccessModal from "./features/pwa/PwaInstallSuccessModal";
import { useSelector, useDispatch } from "react-redux";
import { closeModal, hideConfirmation } from "./features/ui/uiSlice";
import {
  CrossmintProvider,
  CrossmintAuthProvider,
  CrossmintWalletProvider,
} from "@crossmint/client-sdk-react-ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./config/queryClient";
import { CROSSMINT_ENVIRONMENT } from "./features/deposit/crossmintDepositConfig";
import ProductionWalletBootstrap from "./features/deposit/ProductionWalletBootstrap";
import SwapSignerOnboarding from "./features/deposit/SwapSignerOnboarding";
import { APP_ROUTES } from "@/constants/app";
import usePwaInstallSync from "./hooks/usePwaInstallSync";
import { useIntercomMessenger } from "@/hooks/useIntercomMessenger";
import { useAuth as useAuthState } from "./hooks/useAuth";
import { useUserStore } from "./store/useUserStore";
import {
  initGoogleAnalytics,
  setGoogleAnalyticsUserId,
  trackPageView,
} from "./utils/googleAnalytics";
import {
  getPendingReferralCode,
  getReferralCodeFromSearch,
  storePendingReferralCode,
} from "./utils/referralCode";

const PWA_SYNC_EVENT = "finu:pwa-install-synced";

const RootRedirect = () => {
  const location = useLocation();
  const {
    isAuthenticated: reduxIsAuthenticated,
    isLoading,
    user: reduxUser,
  } = useAuthState();
  const storeToken = useUserStore((state) => state.token);
  const storeUser = useUserStore((state) => state.user);
  const hasLocalToken = Boolean(localStorage.getItem("token"));
  const hasToken = reduxIsAuthenticated || Boolean(storeToken) || hasLocalToken;
  const hasResolvedUser = Boolean(reduxUser || storeUser);
  const referralCode = getReferralCodeFromSearch(location.search);

  // Keep a referral through in-app redirects and external auth callbacks.
  // It is cleared only after the backend sign-in succeeds.
  if (!hasToken && referralCode) {
    storePendingReferralCode(referralCode);
  }

  if (isLoading && hasToken && !hasResolvedUser) {
    return <div className="min-h-screen bg-white" aria-hidden="true" />;
  }

  const pendingReferralCode = referralCode || getPendingReferralCode();
  const loginPath = pendingReferralCode
    ? `/login?ref=${encodeURIComponent(pendingReferralCode)}`
    : "/login";

  return <Navigate to={hasToken ? "/home" : loginPath} replace />;
};

const NotFoundRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const ref = params.get("ref");
  const rootParams = new URLSearchParams();
  if (ref) rootParams.set("ref", ref);
  const rootUrl = rootParams.toString() ? `/?${rootParams.toString()}` : "/";

  return <Navigate to={rootUrl} replace />;
};

const LegacyTransferRedirect = () => {
  const location = useLocation();
  const isWithdrawal = new URLSearchParams(location.search).get("redirect") === "withdraw";
  return <Navigate to={isWithdrawal ? "/withdraw/card" : "/deposit"} replace />;
};

const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const WelcomePage = React.lazy(() => import("./pages/WelcomePage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const EmailVerificationSentPage = React.lazy(
  () => import("./pages/EmailVerificationSentPage"),
);
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const HomePage = React.lazy(() => import("./pages/HomePage"));
const AdminPage = React.lazy(() => import("./pages/admin"));
const AdminUserPage = React.lazy(() => import("./pages/admin/user"));
const AdminReferralPage = React.lazy(() => import("./pages/admin/referral"));
const OnboardingPage = React.lazy(() => import("./pages/OnboardingPage"));
const TransactionsPage = React.lazy(() => import("./pages/TransactionsPage"));
const GoalsPage = React.lazy(() => import("./pages/GoalsPage"));
const BudgetsPage = React.lazy(() => import("./pages/BudgetsPage"));
const BudgetDetailPage = React.lazy(() => import("./pages/BudgetDetailPage"));
const CreateBudgetPage = React.lazy(() => import("./pages/CreateBudgetPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const AICoachPage = React.lazy(() => import("./pages/AICoachPage"));
const TokensPage = React.lazy(() => import("./pages/TokensPage"));
const HelpPage = React.lazy(() => import("./pages/HelpPage"));
const TicketPage = React.lazy(() => import("./pages/TicketPage"));
const OAuthCallbackPage = React.lazy(() => import("./pages/OAuthCallbackPage"));
const SubscriptionSuccessPage = React.lazy(
  () => import("./pages/SubscriptionSuccessPage"),
);
const EmailVerificationPage = React.lazy(
  () => import("./pages/EmailVerificationPage"),
);
const ForgotPasswordPage = React.lazy(
  () => import("./pages/ForgotPasswordPage"),
);
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const InvitePage = React.lazy(() => import("./pages/InvitePage"));
const CommunityPage = React.lazy(() => import("./pages/CommunityPage"));
const AboutUsPage = React.lazy(() => import("./pages/AboutUsPage"));
const PolicyWebViewPage = React.lazy(
  () => import("./pages/PolicyWebViewPage"),
);
const CrossmintDepositPage = React.lazy(() => import("./pages/CrossmintDepositPage"));
const CrossmintWithdrawPage = React.lazy(() => import("./pages/CrossmintWithdrawPage"));
const WithdrawSourcePage = React.lazy(() => import("./pages/WithdrawSourcePage"));
const CrossmintSwapPage = React.lazy(() => import("./pages/CrossmintSwapPage"));
const CrossmintTestnetBalancesPage = React.lazy(() => import("./pages/CrossmintTestnetBalancesPage"));
const CardLimitsPage = React.lazy(() => import("./pages/CardLimitsPage"));
const CardSettingsPage = React.lazy(() => import("./pages/CardSettingsPage"));
const TransactionHistoryPage = React.lazy(
  () => import("./pages/TransactionHistory"),
);
const RefRulesPage = React.lazy(() => import("./pages/RefRule"));
const RewardDetailsPage = React.lazy(() => import("./pages/RewardDetails"));
const RefUsersPage = React.lazy(() => import("./pages/RefUsers"));
const VerifyPage = React.lazy(() => import("./pages/VeryfyPage"));
const VerificationCallbackPage = React.lazy(() => import("./pages/VerifyPage"));

const CardPage = React.lazy(() => import("./pages/CardPage"));
const MyCardsPage = React.lazy(() => import("./pages/MyCardsPage"));
const CardApplicationPage = React.lazy(
  () => import("./pages/CardApplicationPage"),
);
const CardTransactionDetailPage = React.lazy(
  () => import("./pages/CardTransactionDetailPage"),
);
const TransactionDetailPage = React.lazy(
  () => import("./pages/TransactionDetailPage"),
);
const StatementListPage = React.lazy(() => import("./pages/StatementListPage"));
const StatementPage = React.lazy(() => import("./pages/StatementPage"));
const StatementHistoryPage = React.lazy(
  () => import("./pages/StatementHistoryPage"),
);
const NotiPage = React.lazy(() => import("./pages/NotiPage"));
const NotiDetailPage = React.lazy(() => import("./pages/NotiDetailPage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const IdentityVerificationPage = React.lazy(
  () => import("./pages/IdentityVerificationPage"),
);
const KycPersonalDetailsPage = React.lazy(
  () => import("./pages/KycPersonalDetailsPage"),
);
const PhoneAuthenticationPage = React.lazy(
  () => import("./pages/PhoneAuthenticationPage"),
);
const ReferralPage = React.lazy(() => import("./pages/ReferralPage"));
const BudgetSettingsPage = React.lazy(
  () => import("./pages/BudgetSettingsPage"),
);
const TierPricingPage = React.lazy(() => import("./pages/TierPricingPage"));
const ApplyCardPage = React.lazy(() => import("./pages/ApplyCard"));
const WithdrawPage = React.lazy(() => import("./pages/WithdrawPage"));
const publicRoutes = [
  { path: "/admin/login", Component: LoginPage },
  { path: "/login", Component: WelcomePage },
  { path: "/register", Component: RegisterPage },
  {
    path: "/email-verification-sent",
    Component: EmailVerificationSentPage,
  },
  { path: "/oauth-callback", Component: OAuthCallbackPage },
  { path: "/verify-email", Component: EmailVerificationPage },
  { path: "/forgot-password", Component: ForgotPasswordPage },
  { path: "/reset-password", Component: ResetPasswordPage },
  {
    path: "/subscription/success",
    Component: SubscriptionSuccessPage,
  },
];

const protectedRoutes = [
  // Home / dashboard style views
  {
    path: "/verification/callback",
    Component: VerificationCallbackPage,
    requireOnboarding: false,
  },
  {
    path: APP_ROUTES.ADMIN,
    Component: AdminPage,
    requireOnboarding: false,
    requireAdmin: true,
  },
  {
    path: APP_ROUTES.ADMIN_USER,
    Component: AdminUserPage,
    requireOnboarding: false,
    requireAdmin: true,
  },
  {
    path: APP_ROUTES.ADMIN_REFERRAL,
    Component: AdminReferralPage,
    requireOnboarding: false,
    requireAdmin: true,
  },
  {
    path: "/onboarding",
    Component: OnboardingPage,
    requireOnboarding: false,
  },
  {
    path: "/home",
    Component: HomePage,
    requireOnboarding: false,
  },
  {
    path: "/insights",
    Component: DashboardPage,
    requireOnboarding: false,
  },

  // Financial features (onboarding via modal where needed)
  { path: "/card/apply", Component: ApplyCardPage, requireOnboarding: false },
  {
    path: "/card/transactions/:transactionId",
    Component: CardTransactionDetailPage,
    requireOnboarding: false,
  },
  {
    path: "/transaction/:transactionId",
    Component: TransactionDetailPage,
    requireOnboarding: false,
  },
  {
    path: "/card/transactions",
    Component: CardApplicationPage,
    requireOnboarding: false,
  },
  {
    path: "/card/statement-list",
    Component: StatementListPage,
    requireOnboarding: false,
  },
  {
    path: "/card/statement",
    Component: StatementPage,
    requireOnboarding: false,
  },
  {
    path: "/card/statement-history",
    Component: StatementHistoryPage,
    requireOnboarding: false,
  },
  {
    path: "/card/my-cards",
    Component: MyCardsPage,
    requireOnboarding: false,
  },
  {
    path: "/profile",
    Component: ProfilePage,
    requireOnboarding: false,
  },
  {
    path: "/profile/identity-verification",
    Component: IdentityVerificationPage,
    requireOnboarding: false,
  },
  {
    path: "/profile/identity-verification/personal-details",
    Component: KycPersonalDetailsPage,
    requireOnboarding: false,
  },
  {
    path: "/profile/phone-authentication",
    Component: PhoneAuthenticationPage,
    requireOnboarding: false,
  },
  {
    path: "/profile/referral",
    Component: ReferralPage,
    requireOnboarding: false,
  },
  {
    path: "/budget-settings",
    Component: BudgetSettingsPage,
    requireOnboarding: false,
  },
  {
    path: "/membership",
    Component: TierPricingPage,
    requireOnboarding: false,
  },
  {
    path: "/community",
    Component: CommunityPage,
    requireOnboarding: false,
  },
  {
    path: "/about-us",
    Component: AboutUsPage,
    requireOnboarding: false,
  },
  {
    path: "/about-us/privacy-policy",
    Component: PolicyWebViewPage,
    requireOnboarding: false,
  },
  {
    path: "/about-us/terms",
    Component: PolicyWebViewPage,
    requireOnboarding: false,
  },
  {
    path: "/learn/identity-authentication",
    Component: PolicyWebViewPage,
    requireOnboarding: false,
  },
  {
    path: "/notification",
    Component: NotiPage,
    requireOnboarding: false,
  },
  {
    path: "/notification/:id",
    Component: NotiDetailPage,
    requireOnboarding: false,
  },
  {
    path: "/card",
    Component: CardPage,
    requireOnboarding: false,
  },
  {
    path: "/verify",
    Component: VerifyPage,
    requireOnboarding: false,
  },
  {
    path: "/rewards",
    Component: RewardDetailsPage,
    requireOnboarding: false,
  },
  {
    path: "/ref-rules",
    Component: RefRulesPage,
    requireOnboarding: false,
  },
  {
    path: "/refer/users",
    Component: RefUsersPage,
    requireOnboarding: false,
  },
  {
    path: "/transaction-history",
    Component: TransactionHistoryPage,
    requireOnboarding: false,
  },
  {
    path: "/select-network",
    Component: LegacyTransferRedirect,
    requireOnboarding: false,
  },
  {
    path: "/select-token",
    Component: LegacyTransferRedirect,
    requireOnboarding: false,
  },
  {
    path: "/card/limits",
    Component: CardLimitsPage,
    requireOnboarding: false,
  },
  {
    path: "/card/settings",
    Component: CardSettingsPage,
    requireOnboarding: false,
  },
  {
    path: "/deposit",
    Component: CrossmintDepositPage,
    requireOnboarding: false,
  },
  {
    path: "/deposit/wallet",
    Component: CrossmintDepositPage,
    requireOnboarding: false,
  },
  {
    path: "/wallet/balances",
    Component: CrossmintTestnetBalancesPage,
    requireOnboarding: false,
  },
  {
    path: "/wallet/swap",
    Component: CrossmintSwapPage,
    requireOnboarding: false,
  },
  {
    path: "/withdraw",
    Component: WithdrawSourcePage,
    requireOnboarding: false,
  },
  {
    path: "/withdraw/card",
    Component: WithdrawPage,
    requireOnboarding: false,
  },
  {
    path: "/withdraw/wallet",
    Component: CrossmintWithdrawPage,
    requireOnboarding: false,
  },
  {
    path: "/refer",
    Component: InvitePage,
    requireOnboarding: false,
  },
  {
    path: "/transactions",
    Component: TransactionsPage,
    requireOnboarding: false,
    onboardingMode: "modal",
  },
  {
    path: "/goals",
    Component: GoalsPage,
    requireOnboarding: false,
    onboardingMode: "modal",
  },
  {
    path: "/budget",
    Component: BudgetsPage,
    requireOnboarding: false,
    onboardingMode: "modal",
  },
  {
    path: "/budget/new",
    Component: CreateBudgetPage,
    requireOnboarding: false,
    onboardingMode: "modal",
  },
  {
    path: "/budget/category/:categoryId",
    Component: BudgetDetailPage,
    requireOnboarding: false,
    onboardingMode: "modal",
  },

  // AI coach & account related
  {
    path: "/ask",
    Component: AICoachPage,
    requireOnboarding: false,
  },
  {
    path: "/tokens",
    Component: TokensPage,
    requireOnboarding: false,
  },
  {
    path: "/settings",
    Component: SettingsPage,
    requireOnboarding: false,
  },
  {
    path: "/help",
    Component: HelpPage,
    requireOnboarding: false,
  },
  {
    path: "/help/ticket",
    Component: TicketPage,
    requireOnboarding: false,
  },
];

const AppContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { modals, confirmationData } = useSelector((state) => state.ui);
  const { user, token } = useSelector((state) => state.auth);
  const [showPwaSuccessModal, setShowPwaSuccessModal] = useState(false);
  usePwaInstallSync();
  useIntercomMessenger();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    initGoogleAnalytics();
  }, []);

  useEffect(() => {
    if (user?.id || user?.user_id) {
      setGoogleAnalyticsUserId(user.id || user.user_id);
    }
  }, [user?.id, user?.user_id]);

  useEffect(() => {
    trackPageView({
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handlePwaSync = (event) => {
      const updatedUser = event?.detail?.updatedUser;
      const isPwaInstallUpgrade =
        updatedUser?.tierSource === "pwa_install" &&
        updatedUser?.tier === "plus" &&
        updatedUser?.tierActivatedAt;

      if (event?.detail?.isFirstSync || isPwaInstallUpgrade) {
        setShowPwaSuccessModal(true);
      }
    };

    window.addEventListener(PWA_SYNC_EVENT, handlePwaSync);
    return () => window.removeEventListener(PWA_SYNC_EVENT, handlePwaSync);
  }, []);

  const handleSelectPlan = (plan) => {
    dispatch(closeModal("subscription"));
  };

  const handleConfirmationConfirm = () => {
    if (confirmationData?.onConfirm) {
      confirmationData.onConfirm();
    }
    dispatch(hideConfirmation());
  };

  const handleConfirmationClose = () => {
    dispatch(hideConfirmation());
  };

  const handleClosePwaSuccess = () => {
    setShowPwaSuccessModal(false);
  };

  const handleGoToAskFinu = () => {
    setShowPwaSuccessModal(false);
    navigate("/home");
  };

  return (
    <>
      <Routes>
        {/* Public routes */}
        {publicRoutes.map(({ path, Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}

        {/* Protected routes */}
        {protectedRoutes.map(
          ({
            path,
            Component,
            requireOnboarding,
            onboardingMode,
            requireAdmin,
          }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute
                  requireOnboarding={requireOnboarding}
                  onboardingMode={onboardingMode}
                  requireAdmin={requireAdmin}
                >
                  <Component />
                </ProtectedRoute>
              }
            />
          ),
        )}

        {/* Default redirects */}
        <Route path="/deposit/crossmint" element={<Navigate to="/deposit/wallet" replace />} />
        <Route path="/wallet/testnet-balances" element={<Navigate to="/wallet/balances" replace />} />
        <Route path="/ai-coach" element={<Navigate to="/ask" replace />} />
        <Route path="/invite" element={<Navigate to="/refer" replace />} />
        <Route
          path="/profile/tier-pricing"
          element={<Navigate to="/membership" replace />}
        />
        <Route
          path="/profile/budget-settings"
          element={<Navigate to="/budget-settings" replace />}
        />
        <Route path="/dashboard" element={<Navigate to="/insights" replace />} />
        <Route path="/budgets" element={<Navigate to="/budget" replace />} />
        <Route path="/budgets/new" element={<Navigate to="/budget/new" replace />} />
        <Route
          path="/budgets/category/:categoryId"
          element={<Navigate to="/budget" replace />}
        />
        <Route path="/ref-users" element={<Navigate to="/refer/users" replace />} />
        <Route
          path="/reward-details"
          element={<Navigate to="/rewards" replace />}
        />
        <Route
          path="/"
          element={
            <RootRedirect />
          }
        />
        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>

      <SwapSignerOnboarding userId={token ? user?.id : null} />

      {/* Subscription Modal */}
      {/* <SubscriptionModal
        isOpen={modals.subscription}
        onClose={() => dispatch(closeModal("subscription"))}
        onSelectPlan={handleSelectPlan}
      /> */}
      <RewardModal />
      <WelcomeRewardGate />
      <PwaInstallSuccessModal
        open={showPwaSuccessModal}
        onClose={handleClosePwaSuccess}
        onGoToAskFinu={handleGoToAskFinu}
      />
      <ConfirmationModal
        isOpen={modals.confirmation}
        onClose={handleConfirmationClose}
        onConfirm={handleConfirmationConfirm}
        title={confirmationData?.title}
        message={confirmationData?.message}
        confirmText={confirmationData?.confirmText}
        cancelText={confirmationData?.cancelText}
        variant={confirmationData?.variant}
        icon={confirmationData?.icon}
      />
    </>
  );
};

const clientApiKey = import.meta.env.VITE_CROSSMINT_CLIENT_ID;
const stagingWalletOnLogin = {
  chain: "base-sepolia",
  signer: { type: "email" },
};

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [splashFadeOut, setSplashFadeOut] = useState(false);

  useEffect(() => {
    // Hold the splash for 1s, then start the fade-out and unmount once the
    // 500ms fade transition has finished — avoids the abrupt disappearance.
    const fadeTimer = setTimeout(() => setSplashFadeOut(true), 1000);
    const removeTimer = setTimeout(() => setShowSplash(false), 1500);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, []);

  useEffect(() => {
    let reloadedForChunkError = false;
    const CHUNK_RELOAD_GUARD_KEY = "finu:chunk-reload-guard";

    const shouldReloadForModuleFailure = (value) => {
      const message = String(value?.message || value || "");
      return (
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("Failed to load module script") ||
        message.includes("Importing a module script failed")
      );
    };

    const canReloadForChunkError = () => {
      try {
        const raw = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
        const now = Date.now();
        if (!raw) {
          sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(now));
          return true;
        }

        const last = Number(raw);
        // Prevent back-to-back reload loops; allow another auto reload after 30s.
        if (Number.isFinite(last) && now - last < 30_000) {
          return false;
        }

        sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(now));
        return true;
      } catch {
        // If sessionStorage is unavailable, keep prior behavior.
        return true;
      }
    };

    const handleVitePreloadError = (event) => {
      if (reloadedForChunkError) return;
      if (!canReloadForChunkError()) return;
      reloadedForChunkError = true;
      event?.preventDefault?.();
      window.location.reload();
    };

    const handleWindowError = (event) => {
      if (reloadedForChunkError) return;
      if (!shouldReloadForModuleFailure(event?.error || event?.message)) return;
      if (!canReloadForChunkError()) return;
      reloadedForChunkError = true;
      event?.preventDefault?.();
      window.location.reload();
    };

    window.addEventListener("vite:preloadError", handleVitePreloadError);
    window.addEventListener("error", handleWindowError);

    return () => {
      window.removeEventListener("vite:preloadError", handleVitePreloadError);
      window.removeEventListener("error", handleWindowError);
    };
  }, []);

  if (showSplash) {
    return <SplashScreen fadeOut={splashFadeOut} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <CrossmintProvider apiKey={clientApiKey}>
        <CrossmintAuthProvider
          loginMethods={["google", "email", "twitter"]}
          authModalTitle=""
          appearance={{
            spacingUnit: "8px",
            borderRadius: "12px",
            colors: {
              inputBackground: "#fff",
              // Black social buttons (Cleo-style); white label forced via the
              // scoped #crossmint_form button CSS in index.css.
              buttonBackground: "#111111",
              backgroundSecondary: "#bbd1fa",
              border: "#E3E8EF",
              background: "#fff",
              textPrimary: "#18181B",
              textSecondary: "#18181B",
              textLink: "#1400cb",
              danger: "#ff3333",
              accent: "#497AFF",
            },
          }}
        >
          <CrossmintWalletProvider
            createOnLogin={CROSSMINT_ENVIRONMENT === "production" ? undefined : stagingWalletOnLogin}
          >
            <ProductionWalletBootstrap>
              <Provider store={store}>
                <Router>
                  <div className="App">
                    <Suspense
                      fallback={<div className="min-h-screen bg-white" />}
                    >
                      <AppContent />
                    </Suspense>

                    {/* Toast notifications */}
                    <Toaster
                      position="top-right"
                      toastOptions={{
                        duration: 3000,
                        style: {
                          background: "#1f2937",
                          color: "#fff",
                          fontSize: "14px",
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </div>
                </Router>
              </Provider>
            </ProductionWalletBootstrap>
          </CrossmintWalletProvider>
        </CrossmintAuthProvider>
      </CrossmintProvider>
    </QueryClientProvider>
  );
}

export default App;
